import {
  getMembershipConfig,
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
  Offering,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import theme, { panelStyle, shadows } from "@/theme";
import {
  convertPxToRem,
  formatAmount,
  nativeVariantKey,
  navigateToBinahScan,
} from "@/utils";
import { Box, Stack, Typography, Theme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent, trackUserInteraction } from "@/services/analytics-adapter";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import MessagesIconCTA from "../MessagesIconCTA";
import ProfileIconCTA from "../ProfileIconCTA";
import PayGMedicalAssistanceCTA from "../PayGMedicalAssistanceCTA";
import { HealthProfileTile } from "../DashboardTiles";
import LifestyleAndAdviceSection from "../LifestyleAndAdviceSection";
import { TFunction } from "i18next";

interface PayGMemberDashboardProps {
  memberProfile: MemberProfile | null;
  isMemberProfileLoading: boolean;
  memberProfileFetchError: boolean;
}

interface NonFeaturedPanelProps {
  item: any;
  onPanelClick: (route: string, name: string) => void;
}

interface FeaturedPanelProps {
  item: any;
  onPanelClick: (route: string, name: string) => void;
}

function buildServiceOfferingOptions(
  isPathologyEnabled: boolean,
  isVoucherEnabled: boolean,
  t: TFunction
) {
  return [
    {
      type: DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT,
      name: t("PayGMemberDashboard.doctorAndNurseConsult.panel.title"),
      description: t(
        "PayGMemberDashboard.doctorAndNurseConsult.panel.subtitle"
      ),
      route: "/home/get-medical-assistance/intercare",
      leftButton: (
        <Typography fontWeight={600} color="info">
          {t("PayGMemberDashboard.serviceOffering.panel.price", {
            priceFormatted: "R179",
          })}
        </Typography>
      ),
      rightButton: (
        <Typography fontWeight={600} color="primary">
          {t(
            "PayGMemberDashboard.doctorAndNurseConsult.panel.rightButton.title"
          )}
        </Typography>
      ),
    },
    ...(isPathologyEnabled
      ? [
          {
            type: DashboardServiceOfferingTypes.BLOOD_TESTS,
            name: t("PayGMemberDashboard.bloodTests.panel.title"),
            description: t("PayGMemberDashboard.bloodTests.panel.subtitle"),
            route: "/home/get-medical-assistance/pathology",
            leftButton: (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: "R184",
                })}
              </Typography>
            ),
            rightButton: (
              <Typography fontWeight={600} color="primary">
                {t("PayGMemberDashboard.bloodTests.panel.rightButton.title")}
              </Typography>
            ),
          },
        ]
      : []),
    ...(isVoucherEnabled
      ? [
          {
            type: DashboardServiceOfferingTypes.BUY_VOUCHER,
            name: t("PayGMemberDashboard.Voucher.panel.title"),
            description: t("PayGMemberDashboard.Voucher.panel.description"),
            route: "/home/get-medical-assistance/vouchers",
            leftButton: (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.Voucher.panel.price", {
                  priceFormatted: "R179",
                })}
              </Typography>
            ),
            rightButton: (
              <Typography fontWeight={600} color="primary">
                {t("PayGMemberDashboard.Voucher.panel.rightButton.title")}
              </Typography>
            ),
          },
        ]
      : []),
    {
      type: DashboardServiceOfferingTypes.HEALTH_CHECK,
      name: t("PayGMemberDashboard.healthCheck.panel.title"),
      description: t(
        nativeVariantKey("PayGMemberDashboard.healthCheck.panel.subtitle")
      ),
      route: "/binah-scan?skipScanPrompt=true",
      leftButton: (
        <Typography fontWeight={600} color="info">
          {t("common.free")}
        </Typography>
      ),
      rightButton: (
        <Typography fontWeight={600} color="primary">
          {t("PayGMemberDashboard.healthCheck.panel.rightButton.title")}
        </Typography>
      ),
    },
  ];
}

function buildFeaturedServiceOfferingOptions(
  memberProfile: MemberProfile | null,
  t: TFunction
) {
  if (!memberProfile) {
    return [];
  }
  return (
    memberProfile.offerings
      ?.filter((offering) => offering.isFeatured === true)
      .map((offering) => ({
        type:
          offering.chargeCode ===
          MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
            ? DashboardServiceOfferingTypes.DOC_MED_BUNDLE
            : offering.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
              ? DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT
              : offering.chargeCode ===
                  MemberProductPaymentRatesChargeCode.VOUCHER_ONLINE_DOCTOR_CONSULTATION
                ? DashboardServiceOfferingTypes.BUY_VOUCHER
                : "",
        name:
          offering.chargeCode ===
          MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
            ? t("PayGMemberDashboard.DocMedBundle.panel.title")
            : offering.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
              ? t("PayGMemberDashboard.doctorAndNurseConsult.panel.title")
              : offering.chargeCode ===
                  MemberProductPaymentRatesChargeCode.VOUCHER_ONLINE_DOCTOR_CONSULTATION
                ? t("PayGMemberDashboard.Voucher.panel.title")
                : "",
        description:
          offering.chargeCode ===
          MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
            ? "PayGMemberDashboard.DocMedBundle.panel.subtitle"
            : offering.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
              ? t("PayGMemberDashboard.doctorAndNurseConsult.panel.subtitle")
              : offering.chargeCode ===
                  MemberProductPaymentRatesChargeCode.VOUCHER_ONLINE_DOCTOR_CONSULTATION
                ? t("PayGMemberDashboard.Voucher.panel.description")
                : "",
        route: "/home/get-medical-assistance/intercare?isDocMedsBundle=true",
        amount: offering.amount,
        currencyCode: offering.currencyCode,
        isDiscountApplied: offering.isDiscountApplied,
        originalAmount: offering.originalAmount,
        leftButton: (
          <Typography fontWeight={600} color="info">
            {t("PayGMemberDashboard.FeaturedServiceOffering.panel.price", {
              priceFormatted: formatAmount(
                offering.amount || 0,
                offering.currencyCode || ""
              ),
            })}
          </Typography>
        ),
        rightButton: (
          <Typography fontWeight={600} color="primary">
            {t(
              "PayGMemberDashboard.FeaturedServiceOffering.panel.rightButton.title"
            )}
          </Typography>
        ),
      })) || []
  );
}

enum DashboardServiceOfferingTypes {
  DOCTOR_AND_NURSE_CONSULT = "DOCTOR_AND_NURSE_CONSULT",
  BLOOD_TESTS = "BLOOD_TESTS",
  HEALTH_CHECK = "HEALTH_CHECK",
  BUY_VOUCHER = "BUY_VOUCHER",
  DOC_MED_BUNDLE = "DOC_MED_BUNDLE",
}

export default function PayGMemberDashboard({
  memberProfile,
  isMemberProfileLoading,
  memberProfileFetchError,
}: PayGMemberDashboardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isIntercareSupported, setIsIntercareSupported] = useState<
    boolean | null
  >(null);
  const [hasActiveSessions, setHasActiveSessions] = useState<boolean>(false);

  useEffect(() => {
    if (memberProfile?.consultation.consultationResponses) {
      setHasActiveSessions(
        memberProfile.consultation.consultationResponses.length > 0
      );
    }
  }, [memberProfile?.consultation.consultationResponses]);

  const isPathologyEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_PATHOLOGY_PANELS
  );

  const isVoucherEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
  );

  const isDocMedBundleEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_DOC_MED_BUNDLES
  );

  const serviceOfferingOptions = useMemo(
    () => buildServiceOfferingOptions(isPathologyEnabled, isVoucherEnabled, t),
    [isPathologyEnabled, isVoucherEnabled, t]
  );

  const featuredServiceOfferingOptions = useMemo(
    () => buildFeaturedServiceOfferingOptions(memberProfile, t),
    [memberProfile, t]
  );

  function onBellIconButtonClick() {
    navigate("/home/messages");
  }

  function onProfileIconCTAClick() {
    trackEvent({
      event: "action.myProfileCTAClicked",
      source: "home screen",
    });

    trackUserInteraction({
      linkText: "Home screen | My profile",
      linkIntent: "navigational",
      linkScope: "button",
    });

    navigate("/home/my-profile");
  }

  function onPanelClick(route: string, name: string) {
    trackUserInteraction({
      linkText: `Home screen | ${name}`,
      linkIntent: "navigational",
      linkScope: "button",
    });

    if (route.startsWith("/binah-scan")) {
      navigateToBinahScan(route);
      return;
    }

    navigate(route);
  }

  function getFeaturedServiceOfferingPriceCTA(
    type: DashboardServiceOfferingTypes,
    amount: number,
    currencyCode: string,
    discountApplied: boolean,
    originalAmount: number
  ) {
    if (type === DashboardServiceOfferingTypes.DOC_MED_BUNDLE) {
      const isDiscountApplied = Boolean(discountApplied) || false;
      const amountFormatted = formatAmount(amount || 0, currencyCode || "");
      const originalAmountFormatted = formatAmount(
        originalAmount || 0,
        currencyCode || ""
      );
      return (
        <Panel
          sx={{
            ...(isDiscountApplied === true && {
              paddingRight: convertPxToRem(30),
              paddingTop: convertPxToRem(5),
              paddingBottom: convertPxToRem(5),
            }),
            boxShadow: "none",
            backgroundColor: (theme: Theme) =>
              isDiscountApplied
                ? theme.palette.accent4.main
                : theme.palette.primary.main,
          }}
        >
          <>
            {!isDiscountApplied ? (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.FeaturedServiceOffering.panel.price", {
                  priceFormatted: amountFormatted,
                })}
              </Typography>
            ) : (
              <Stack color="info" textAlign="left">
                <Typography fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.FeaturedServiceOffering.panel.originalAmount.price",
                    {
                      priceFormatted: amountFormatted,
                    }
                  )}
                </Typography>
                <Typography
                  fontWeight={600}
                  color="info"
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {t(
                    "PayGMemberDashboard.FeaturedServiceOffering.panel.discount.price",
                    {
                      priceFormatted: originalAmountFormatted,
                    }
                  )}
                </Typography>
              </Stack>
            )}
          </>
        </Panel>
      );
    }
  }

  function getServiceOfferingPriceCTA(type: DashboardServiceOfferingTypes) {
    if (type === DashboardServiceOfferingTypes.HEALTH_CHECK) {
      return (
        <Panel
          sx={{
            boxShadow: "none",
            height: "fit-content",
            backgroundColor: (theme: Theme) => theme.palette.primary.main,
          }}
        >
          <Typography fontWeight={600} color="info">
            {t("common.free")}
          </Typography>
        </Panel>
      );
    } else if (
      type === DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT
    ) {
      const isDiscountApplied =
        Boolean(memberProfile?.consultation?.isDiscountApplied) || false;

      const amountFormatted = memberProfile?.consultation.consultationRates
        .find(
          (item) =>
            item.chargeCode ===
            MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
        )
        ?.amountFormatted?.replace(".00", "");

      const originalAmountFormatted =
        memberProfile?.consultation.consultationRates
          .find(
            (item) =>
              item.chargeCode ===
              MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
          )
          ?.originalAmountFormatted?.replace(".00", "");

      return (
        <Panel
          sx={{
            ...(isDiscountApplied === true && {
              paddingRight: convertPxToRem(30),
              paddingTop: convertPxToRem(5),
              paddingBottom: convertPxToRem(5),
            }),
            boxShadow: "none",
            backgroundColor: (theme: Theme) =>
              isDiscountApplied
                ? theme.palette.accent4.main
                : theme.palette.primary.main,
          }}
        >
          <>
            {!isDiscountApplied ? (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: amountFormatted,
                })}
              </Typography>
            ) : (
              <Stack color="info" textAlign="left">
                <Typography fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.originalAmount.price",
                    {
                      priceFormatted: amountFormatted,
                    }
                  )}
                </Typography>
                <Typography
                  fontWeight={600}
                  color="info"
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.discount.price",
                    {
                      priceFormatted: originalAmountFormatted,
                    }
                  )}
                </Typography>
              </Stack>
            )}
          </>
        </Panel>
      );
    } else if (type === DashboardServiceOfferingTypes.BUY_VOUCHER) {
      const isDiscountApplied =
        Boolean(memberProfile?.voucher?.isDiscountApplied) || false;

      const currencyCode = memberProfile?.voucher?.rates?.find(
        (item) =>
          item.chargeCode ===
          MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
      )?.currencyCode;

      const amountFormatted = memberProfile?.voucher?.rates?.find(
        (item) =>
          item.chargeCode ===
          MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
      )?.amount;

      const originalAmountFormatted = memberProfile?.voucher?.rates?.find(
        (item) =>
          item.chargeCode ===
          MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
      )?.originalAmount;

      return (
        <Panel
          sx={{
            ...(isDiscountApplied === true && {
              paddingRight: convertPxToRem(30),
              paddingTop: convertPxToRem(5),
              paddingBottom: convertPxToRem(5),
            }),
            boxShadow: "none",
            backgroundColor: (theme: Theme) =>
              isDiscountApplied
                ? theme.palette.accent4.main
                : theme.palette.primary.main,
          }}
        >
          <>
            {!isDiscountApplied ? (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: formatAmount(
                    amountFormatted || 0,
                    currencyCode || ""
                  ),
                })}
              </Typography>
            ) : (
              <Stack color="info" textAlign="left">
                <Typography fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.originalAmount.price",
                    {
                      priceFormatted: formatAmount(
                        amountFormatted || 0,
                        currencyCode || ""
                      ),
                    }
                  )}
                </Typography>
                <Typography
                  fontWeight={600}
                  color="info"
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.discount.price",
                    {
                      priceFormatted: formatAmount(
                        originalAmountFormatted || 0,
                        currencyCode || ""
                      ),
                    }
                  )}
                </Typography>
              </Stack>
            )}
          </>
        </Panel>
      );
    } else if (type === DashboardServiceOfferingTypes.DOC_MED_BUNDLE) {
      memberProfile?.offerings.map((offering: Offering) => {
        const isDiscountApplied = Boolean(offering?.isDiscountApplied) || false;
        const amountFormatted = formatAmount(
          offering?.amount || 0,
          offering.currencyCode || ""
        );
        const originalAmountFormatted = formatAmount(
          offering.originalAmount || 0,
          offering.currencyCode || ""
        );
        return (
          <Panel
            key={offering.offeringId}
            sx={{
              ...(isDiscountApplied === true && {
                paddingRight: convertPxToRem(30),
                paddingTop: convertPxToRem(5),
                paddingBottom: convertPxToRem(5),
              }),
              boxShadow: "none",
              backgroundColor: (theme: Theme) =>
                isDiscountApplied
                  ? theme.palette.accent4.main
                  : theme.palette.primary.main,
            }}
          >
            <>
              {!isDiscountApplied ? (
                <Typography fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.FeaturedServiceOffering.panel.price",
                    {
                      priceFormatted: amountFormatted,
                    }
                  )}
                </Typography>
              ) : (
                <Stack color="info" textAlign="left">
                  <Typography fontWeight={600} color="info">
                    {t(
                      "PayGMemberDashboard.FeaturedServiceOffering.panel.originalAmount.price",
                      {
                        priceFormatted: amountFormatted,
                      }
                    )}
                  </Typography>
                  <Typography
                    fontWeight={600}
                    color="info"
                    variant="body2"
                    sx={{ textDecoration: "line-through" }}
                  >
                    {t(
                      "PayGMemberDashboard.FeaturedServiceOffering.panel.discount.price",
                      {
                        priceFormatted: originalAmountFormatted,
                      }
                    )}
                  </Typography>
                </Stack>
              )}
            </>
          </Panel>
        );
      });
    } else if (type === DashboardServiceOfferingTypes.BLOOD_TESTS) {
      const isDiscountApplied =
        Boolean(memberProfile?.pathology?.isDiscountApplied) || false;

      const amountFormatted = formatAmount(
        memberProfile?.pathology?.amount || 0,
        memberProfile?.pathology?.currencyCode || ""
      );

      const originalAmountFormatted = formatAmount(
        memberProfile?.pathology?.originalAmount || 0,
        memberProfile?.pathology?.currencyCode || ""
      );

      return (
        <Panel
          sx={{
            ...(isDiscountApplied === true && {
              paddingRight: convertPxToRem(30),
              paddingTop: convertPxToRem(5),
              paddingBottom: convertPxToRem(5),
            }),
            boxShadow: "none",
            backgroundColor: (theme: Theme) =>
              isDiscountApplied
                ? theme.palette.accent4.main
                : theme.palette.primary.main,
          }}
        >
          <>
            {!isDiscountApplied ? (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: amountFormatted,
                })}
              </Typography>
            ) : (
              <Stack color="info" textAlign="left">
                <Typography fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.originalAmount.price",
                    {
                      priceFormatted: amountFormatted,
                    }
                  )}
                </Typography>
                <Typography
                  fontWeight={600}
                  color="info"
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.discount.price",
                    {
                      priceFormatted: originalAmountFormatted,
                    }
                  )}
                </Typography>
              </Stack>
            )}
          </>
        </Panel>
      );
    }
  }

  function FeaturedPanel({ item, onPanelClick }: FeaturedPanelProps) {
    return (
      <Stack
        key={item.name}
        sx={{
          ...panelStyle,
          cursor: "pointer",
          backgroundColor: "primary.100",
          border: `2px solid ${theme.palette.primary.main}`,
        }}
        component="button"
        aria-label={item.name}
        spacing={1}
        onClick={() => onPanelClick(item.route, item.name)}
      >
        <Stack
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          gap={1}
          alignItems="left"
        >
          <Typography variant="h3" fontWeight={600} textAlign="left">
            {item.name}
          </Typography>
          <Stack
            sx={{
              backgroundColor: "accent1.main",
              borderRadius: 2,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              variant="body2"
              color="primary.contrastText"
            >
              {t("common.new")}
            </Typography>
          </Stack>
        </Stack>

        <Typography textAlign="left" variant="body1" color="neutral.light">
          <Trans i18nKey={item.description}>
            <Typography
              color="accent1.main"
              fontWeight="bold"
              component="span"
            ></Typography>
          </Trans>
        </Typography>

        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          {getFeaturedServiceOfferingPriceCTA(
            item.type,
            item.amount,
            item.currencyCode,
            item.isDiscountApplied,
            item.originalAmount
          )}
          <ButtonWithAnalytics
            component="div"
            role="presentation"
            variant="text"
            size="small"
            endIcon={<IconLoader icon="ChevronNextIcon" />}
          >
            {item.rightButton}
          </ButtonWithAnalytics>
        </Stack>
      </Stack>
    );
  }

  function NonFeaturedPanel({ item, onPanelClick }: NonFeaturedPanelProps) {
    const hasActiveSessionsAndUnifiedCareEnabled =
      hasActiveSessions &&
      isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE);
    const isDoctorAndNurseConsult =
      item.type === DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT;
    return (
      <Stack
        key={item.name}
        sx={{
          ...panelStyle,
          cursor: "pointer",
          border: "none",
          position: "relative",
        }}
        component="button"
        aria-label={item.name}
        spacing={1}
        onClick={() => onPanelClick(item.route, item.name)}
      >
        <Typography
          textAlign="left"
          variant="h4"
          fontWeight={600}
          fontSize={convertPxToRem(16)}
        >
          {item.name}
        </Typography>

        <Typography
          textAlign="left"
          variant="body1"
          color="neutral.light"
          fontSize={convertPxToRem(14)}
        >
          {item.description}
        </Typography>

        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          {getServiceOfferingPriceCTA(item.type)}
          <ButtonWithAnalytics
            component="div"
            role="presentation"
            variant="text"
            size="small"
            endIcon={<IconLoader icon="ChevronNextIcon" />}
          >
            {isDoctorAndNurseConsult && hasActiveSessionsAndUnifiedCareEnabled
              ? t(
                  "PayGMemberDashboard.doctorAndNurseConsult.panel.continueSessionButton.title"
                )
              : item.rightButton}
          </ButtonWithAnalytics>
        </Stack>

        {isDoctorAndNurseConsult && hasActiveSessionsAndUnifiedCareEnabled && (
          <Stack
            width="calc(100% + 32px)"
            alignItems="center"
            flexDirection="row"
            sx={{
              ...panelStyle,
              boxShadow: "none",
              backgroundColor: theme.palette.accent1[100],
              borderRadius: 0,
              gap: 1,
              position: "relative",
              left: -16,
              bottom: -16,
            }}
          >
            <IconLoader icon="ClockIcon" sx={{ color: "accent1.main" }} />
            <Typography
              fontWeight={600}
              variant="body1"
              color="neutral.dark"
              fontSize={convertPxToRem(14)}
            >
              {t("PayGMemberDashboard.doctorAndNurseConsult.panel.footer.text")}
            </Typography>
          </Stack>
        )}
      </Stack>
    );
  }

  useEffect(() => {
    if (isMemberProfileLoading === false) {
      const membershipConfig = getMembershipConfig(
        memberProfile?.membership?.productCode
      );

      setIsIntercareSupported(
        (isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_INTERCARE_MEDICAL_ASSISTANCE
        ) &&
          !isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE) &&
          membershipConfig?.isIntercareSupported) ||
          false
      );
    }
  }, [memberProfile, isMemberProfileLoading]);

  return (
    <Stack spacing={2}>
      <Box
        p={2}
        gap={2}
        sx={{
          borderRadius: `0 0 ${convertPxToRem(15)}  ${convertPxToRem(15)}`,
          background: `linear-gradient(to bottom, ${theme.palette.primary.contrastText}, ${theme.palette.accent2.light}, ${theme.palette.accent2.light})`,
          width: "100%",
          boxShadow: shadows.light,
        }}
      >
        <Stack direction="row" width="100%" justifyContent="end">
          <Stack display="flex" alignItems="end" position="relative">
            <MessagesIconCTA onClick={onBellIconButtonClick} />
          </Stack>
          <Stack display="flex" alignItems="end" position="relative">
            <ProfileIconCTA onClick={onProfileIconCTAClick} />
          </Stack>
        </Stack>
        <Typography
          variant="body1"
          sx={{
            width: "100%",
            fontSize: convertPxToRem(20),
          }}
        >
          {memberProfile && memberProfile?.firstName?.length > 0
            ? t("Home.title.withName", {
                name: memberProfile.firstName,
              })
            : t("Home.title.withoutName")}
        </Typography>
        <Typography
          variant="h1"
          sx={{
            width: "100%",
            fontSize: convertPxToRem(24),
          }}
        >
          {t("Home.helpSubtitle")}
        </Typography>

        {isIntercareSupported && (
          <Stack mt={1} sx={{ zIndex: 2 }}>
            <PayGMedicalAssistanceCTA
              memberProfile={memberProfile}
              isLoading={isMemberProfileLoading}
              onlyDisplayWhenActiveSession={true}
            />
          </Stack>
        )}
      </Box>

      <Stack sx={{ zIndex: 2 }} spacing={2} p={2}>
        <Stack spacing={2}>
          {isDocMedBundleEnabled &&
            featuredServiceOfferingOptions.length > 0 && (
              <>
                {featuredServiceOfferingOptions.map((item) => (
                  <FeaturedPanel
                    key={item.name}
                    item={item}
                    onPanelClick={onPanelClick}
                  />
                ))}
              </>
            )}
          {serviceOfferingOptions.map((item) => (
            <NonFeaturedPanel
              key={item.name}
              item={item}
              onPanelClick={onPanelClick}
            />
          ))}
        </Stack>
        <Stack spacing={2}>
          <Stack
            justifyContent="space-between"
            flexDirection="row"
            alignItems="center"
          >
            <Typography component="h2" fontWeight={600}>
              {t("common.healthInfo")}
            </Typography>
            <Box
              sx={{
                p: 1,
                background: theme.palette.accent1.main,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="accent1.contrastText"
              >
                {t("common.freeInfo")}
              </Typography>
            </Box>
          </Stack>

          <HealthProfileTile
            lastUpdatedDate={memberProfile?.healthProfile?.lastUpdatedAt}
            healthScore={
              Number(memberProfile?.healthProfile?.healthScoreValue) || 0
            }
            isLoading={isMemberProfileLoading}
            isError={memberProfileFetchError}
            fullWidth
          />

          {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_CHAT_BOT) && (
            <LifestyleAndAdviceSection
              displayTitle={false}
              displayTag={false}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
